import React, { useState, useMemo } from 'react';
import { Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

import { getAcceptLabel, getServiceLabel } from '../../util/Lookups';
import { computeElapsedInfo, computeElapsedInfoFromYYYYMMDD } from '../../util/elapsedTime';
import ServiceBadge from '../atom/ServiceBadge';
import ElapsedPill from '../atom/ElapsedPill';
import StatusBadge from '../atom/StatusBadge';

import { TAB_TYPE, SERVICE_TYPE_LABEL, CORP_TYPE_LABEL, DEFAULT_PAGE_SIZE } from '../../constants';
import type {
  TabType,
  VocBoardItem,
  ServiceBoardItem,
  TransformedVocItem,
  TransformedServiceItem
} from '../../types';

// ========== Types ==========

interface TableListProps {
  filterboard: (VocBoardItem | ServiceBoardItem)[];
  activeTab: TabType;
  totalLength?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  loading?: boolean;
}

type TransformedItem = TransformedVocItem | TransformedServiceItem;

// ========== 데이터 변환 헬퍼 함수 ==========

/**
 * SERVICE 데이터 변환
 */
function transformServiceItem(
  element: ServiceBoardItem,
  id: number,
  nowTime: number
): TransformedServiceItem {
  const requestTypeVal = element.requestType || '';
  const requestTypeDisplay = SERVICE_TYPE_LABEL[requestTypeVal] || requestTypeVal || '-';

  // 법인종류 표시
  const corpTypeVal = element.corpType || '';
  let corpTypeDisplay = '-';

  if (corpTypeVal.includes('-')) {
    const [, typeValue] = corpTypeVal.split('-');
    corpTypeDisplay = CORP_TYPE_LABEL[typeValue] || corpTypeVal;
  } else {
    corpTypeDisplay = CORP_TYPE_LABEL[corpTypeVal] || corpTypeVal || '-';
  }

  // 승인 상태 표시
  const statusVal = String(element.status || '').toLowerCase();
  let statusDisplay = '-';

  if (['pending', '0', '대기'].includes(statusVal)) {
    statusDisplay = '대기';
  } else if (['approved', 'success', '1', '승인'].includes(statusVal)) {
    statusDisplay = '승인';
  } else if (['rejected', 'fail', '2', '반려', '거절'].includes(statusVal)) {
    statusDisplay = '반려';
  }

  // 작성일자 경과정보 계산
  const createdElapsedInfo = computeElapsedInfoFromYYYYMMDD(nowTime, element.createdAt);

  return {
    key: element.requestId ?? id,
    ...element,
    requestTypeDisplay,
    corpTypeDisplay,
    statusDisplay,
    createdElapsed: createdElapsedInfo.status,
    createdElapsedText: createdElapsedInfo.text,
    createdElapsedFull: createdElapsedInfo.full
  };
}

/**
 * VOC 데이터 변환
 */
function transformVocItem(element: VocBoardItem, id: number, nowTime: number): TransformedVocItem {
  const elapsedInfo = computeElapsedInfo(nowTime, element.oriDateTime, element.accept);
  const acceptLabel = getAcceptLabel(element.accept);
  const serviceLabel = getServiceLabel(element.serviceSortation);

  const serviceTypeVal = element.serviceType || element.ServiceType || null;
  const serviceTypeDisplay = serviceTypeVal
    ? SERVICE_TYPE_LABEL[serviceTypeVal] || String(serviceTypeVal)
    : null;

  return {
    key: element.no ?? id,
    ...element,
    status: acceptLabel,
    elapsed: elapsedInfo.status,
    elapsedText: elapsedInfo.text,
    elapsedFull: elapsedInfo.full,
    serviceSortation: serviceLabel,
    serviceType: serviceTypeVal || undefined,
    serviceTypeDisplay,
    empNm: element.empNm || ''
  };
}

// ========== 컬럼 정의 ==========

const vocColumns: ColumnsType<TransformedVocItem> = [
  {
    title: '번호',
    dataIndex: 'no',
    width: 80
  },
  {
    title: '채널',
    dataIndex: 'serviceSortation',
    render: (_, record) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <ServiceBadge code={record._raw?.serviceSortation || record.serviceSortation}>
          {record.serviceTypeDisplay || record.serviceSortation}
        </ServiceBadge>
      </div>
    )
  },
  {
    title: '제목',
    dataIndex: 'title'
  },
  {
    title: '작성자',
    dataIndex: 'name'
  },
  {
    title: '담당자',
    dataIndex: 'empNm'
  },
  {
    title: '조치 상태',
    dataIndex: 'status'
  },
  {
    title: '경과',
    dataIndex: 'elapsed',
    render: (_, record) => (
      <ElapsedPill
        status={record.elapsed}
        text={record.elapsedText}
        tooltipText={record.elapsedFull || record.elapsedText}
      />
    )
  }
];

const serviceColumns: ColumnsType<TransformedServiceItem> = [
  {
    title: '번호',
    dataIndex: 'requestId',
    width: 70
  },
  {
    title: '사업자등록번호',
    dataIndex: 'bizRegNo',
    width: 130,
    render: v => v || '-'
  },
  {
    title: '서비스 종류',
    dataIndex: 'requestTypeDisplay',
    width: 120,
    render: v => v || '-'
  },
  {
    title: '법인명',
    dataIndex: 'corporateName',
    width: 150,
    render: v => v || '-'
  },
  {
    title: '법인종류',
    dataIndex: 'serviceKind',
    width: 140,
    render: v => v || '-'
  },
  {
    title: '제목',
    dataIndex: 'title',
    ellipsis: true
  },
  {
    title: '승인 상태',
    dataIndex: 'statusDisplay',
    width: 90,
    render: v => <StatusBadge status={v || '-'} />
  },
  {
    title: '작성자',
    dataIndex: 'contactName',
    width: 100,
    render: v => v || '-'
  },
  {
    title: '작성일자',
    dataIndex: 'createdAt',
    width: 110,
    render: (_, record) => (
      <ElapsedPill
        status={record.createdElapsed}
        text={record.createdElapsedText}
        tooltipText={record.createdElapsedFull || record.createdElapsedText}
      />
    )
  }
];

// ========== Component ==========

const TableList: React.FC<TableListProps> = ({
  filterboard,
  activeTab,
  totalLength,
  onPageChange,
  loading
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const navigate = useNavigate();

  // 탭에 따라 컬럼 선택
  const columns = activeTab === TAB_TYPE.SERVICE ? serviceColumns : vocColumns;

  // 데이터 변환
  const transformedData = useMemo(() => {
    const nowTime = Date.now();

    return (filterboard || []).map((element, id) => {
      if (activeTab === TAB_TYPE.SERVICE) {
        return transformServiceItem(element as ServiceBoardItem, id, nowTime);
      }
      return transformVocItem(element as VocBoardItem, id, nowTime);
    });
  }, [filterboard, activeTab]);

  const handleRowClick = (record: TransformedItem) => {
    const stateToSend = (record as TransformedVocItem)._raw || record;

    if (activeTab === TAB_TYPE.SERVICE) {
      const requestId = (record as TransformedServiceItem).requestId;
      navigate(`/AdminServiceDetail?requestId=${requestId}`, { replace: false });
    } else {
      navigate('/AdminVocDetail', { replace: false, state: stateToSend });
    }
  };

  return (
    <Table
      columns={columns as ColumnsType<TransformedItem>}
      dataSource={transformedData}
      loading={loading}
      pagination={{
        current: page + 1,
        pageSize: pageSize,
        total: totalLength || 0,
        showSizeChanger: true,
        onChange: (pageNumber, newPageSize) => {
          setPage(pageNumber - 1);
          setPageSize(newPageSize);
          onPageChange?.(pageNumber - 1, newPageSize);
        },
        position: ['bottomCenter']
      }}
      onRow={record => ({
        onClick: () => handleRowClick(record)
      })}
      scroll={{ x: activeTab === TAB_TYPE.SERVICE ? 1100 : undefined }}
    />
  );
};

export default TableList;
